import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiMap, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { formatUtilityDisplayName } from '../../utils/formatUtilityName';
import { setSelectedUtility } from '../../utils/utilitySelection';

// Only California and New York are available in utility module
const ALLOWED_STATE_CODES = ['CA', 'NY'];

// State code -> display name (US + Canada) for dropdown labels
const STATE_DISPLAY_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
    NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
    PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands',
    PR: 'Puerto Rico', VI: 'U.S. Virgin Islands',
    AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', ON: 'Ontario',
    PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', NT: 'Northwest Territories',
    NU: 'Nunavut', YT: 'Yukon',
};

const StateUtilitySelector = () => {
    const navigate = useNavigate();

    const [selectedState, setSelectedState] = useState('');
    const [selectedUtilityName, setSelectedUtilityName] = useState('');
    const [utilityOptionsByState, setUtilityOptionsByState] = useState([]);
    const [loadingUtilities, setLoadingUtilities] = useState(false);
    const [utilityLoadError, setUtilityLoadError] = useState('');

    // State options: only CA and NY for utility module
    const stateOptions = useMemo(() => {
        const codes = [...ALLOWED_STATE_CODES].sort();
        return codes.map(code => ({
            value: code,
            label: STATE_DISPLAY_NAMES[code] || code,
        }));
    }, []);

    // Utility options for the selected state from backend API (includes utility territory IDs)
    const utilityOptions = useMemo(() => {
        return utilityOptionsByState.map((utility) => ({
            value: utility.name,
            label: formatUtilityDisplayName(utility.name),
            id: utility.id,
        }));
    }, [utilityOptionsByState]);

    useEffect(() => {
        const fetchUtilities = async () => {
            if (!selectedState) {
                setUtilityOptionsByState([]);
                setUtilityLoadError('');
                return;
            }

            setLoadingUtilities(true);
            setUtilityLoadError('');

            try {
                const authToken = localStorage.getItem('authToken');
                const response = await axios.get(API_ENDPOINTS.getUtilityTerritories, {
                    headers: authToken ? { Authorization: `Token ${authToken}` } : {},
                    params: {
                        state: selectedState,
                        page_size: 500,
                    },
                });

                const territories = Array.isArray(response.data?.territories)
                    ? response.data.territories
                    : [];

                const seenNames = new Set();
                const options = [];
                territories.forEach((t) => {
                    const name = typeof t?.NAME === 'string' ? t.NAME.trim() : '';
                    const id = t?.id;
                    if (!name || id == null) return;
                    const key = name.toLowerCase();
                    if (seenNames.has(key)) return;
                    seenNames.add(key);
                    options.push({ id, name });
                });

                options.sort((a, b) => a.name.localeCompare(b.name));
                setUtilityOptionsByState(options);
            } catch (error) {
                console.error('Failed to load utilities for state selection:', error);
                setUtilityOptionsByState([]);
                setUtilityLoadError('Unable to load utilities for this state.');
            } finally {
                setLoadingUtilities(false);
            }
        };

        fetchUtilities();
    }, [selectedState]);

    const handleStateChange = (e) => {
        const code = e.target.value;
        setSelectedState(code);
        setSelectedUtilityName('');
    };

    const handleUtilityChange = (e) => {
        setSelectedUtilityName(e.target.value || '');
    };

    const goBack = () => {
        navigate('/home');
    };

    const proceedToNext = () => {
        const selectedUtility = utilityOptions.find((option) => option.value === selectedUtilityName);
        // Persist the selection so the dashboard (and other pages) can acknowledge it.
        setSelectedUtility({
            stateCode: selectedState,
            utilityName: selectedUtilityName,
            utilityTerritoryId: selectedUtility?.id ?? null,
        });
        navigate('/utility/dashboard');
    };

    const canProceed = selectedState && selectedUtilityName;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={goBack}
                                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <FiChevronLeft className="w-5 h-5" />
                                <span>Back</span>
                            </button>
                            <div className="flex items-center space-x-3">
                                <FiMap className="w-8 h-8 text-blue-600" />
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">GridPlan</h1>
                                    <p className="text-sm text-gray-600">Select state and utility</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
                <div className="w-full max-w-md">
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-6">
                            Select State and Utility
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    State
                                </label>
                                <select
                                    value={selectedState}
                                    onChange={handleStateChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Select a state...</option>
                                    {stateOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Utility
                                </label>
                                <select
                                    value={selectedUtilityName}
                                    onChange={handleUtilityChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    disabled={!selectedState || loadingUtilities}
                                >
                                    <option value="">
                                        {loadingUtilities ? 'Loading utilities...' : 'Select a utility...'}
                                    </option>
                                    {utilityOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                {utilityLoadError && (
                                    <p className="mt-2 text-sm text-red-600">{utilityLoadError}</p>
                                )}
                            </div>

                            {canProceed && (
                                <div className="mt-6">
                                    <button
                                        onClick={proceedToNext}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                                    >
                                        Continue
                                        <FiChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StateUtilitySelector;
